import { codeableConcept } from '#src/codeableConcept.ts'

export const SEEN_HEALTH_SYSTEM = 'https://www.seenhealth.org/fhir'
export const ELATION_ID_SYSTEM = 'https://elationemr.com'
export const ELATION_SANDBOX_ID_SYSTEM = 'https://sandbox.elationemr.com'
export const TWENTY_LEAD_ID_SYSTEM =
  'https://sgv.crm.seen.tech/object/opportunity'
export const TWENTY_ATTACHMENT_ID_SYSTEM =
  'https://sgv.crm.seen.tech/attachment'
export const TWENTY_NOTE_ID_SYSTEM = 'https://sgv.crm.seen.tech/note'

export const SINCH_FAX_ID_SYSTEM = 'https://sinch.com/fax'

export const EXTERNAL_PROVIDER_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Practitioner/external-provider`

export const PARTICIPANT_TAG_SYSTEM =
  'https://www.seenhealth.org/fhir/Patient/tag'

export const PROFILE_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Profile/tag`

export const COVERAGE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Coverage`

export const ENCOUNTER_CLASS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/EncounterClass`
export const LOCATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Location`
export const ORGANIZATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Organization`

export const ORGANIZATION_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/organization-type`

export const ORGANIZATION_DESIGNATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/organization-designation`
export const ORGANIZATION_SERVICE_CATEGORY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/organization-service-category`
export const ORGANIZATION_CONTRACT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/organization-contract`
export const ORGANIZATION_AVAILABILITY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/organization-availability`

// US Taxpayer Identification Number (EIN) — HL7-assigned OID.
export const TAX_ID_SYSTEM = 'urn:oid:2.16.840.1.113883.4.4'

export const HEALTHCARE_SERVICE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/HealthcareService`

export const SEEN_HEALTH_ORG_IDENTIFIER = 'seen-health'
export const SGV_LOCATION_IDENTIFIER = 'san-gabriel-valley'

export const TASK_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/TaskCategory`
export const TASK_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/TaskType`

export const COMPOSITION_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CompositionCategory`
export const COMPOSITION_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CompositionType`

// Hard-coded visit-note category vocabulary (assessment-kind codes). Role
// categories reuse PRACTITIONER_ROLE; see models/visitNoteCategory.ts.
export const VISIT_NOTE_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/VisitNoteCategory`

export const ASSESSMENT_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/AssessmentType`

export const INCIDENT_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/IncidentType`

export const ASSESSMENT_SECTION_VALIDATED_TOOL = `${SEEN_HEALTH_SYSTEM}/CodeSystem/assessment-section-validated-tool`

export const PRACTITIONER_ROLE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/Practitioner-Role`

export const CARE_TEAM_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CareTeamCategory`

// Top level, goal, and intervention
export const CARE_PLAN_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CarePlanCategory`

// Initial, semi-annual, change of care
export const CARE_PLAN_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CarePlanType`

export const CARE_PLAN_SORT_ORDER = `${SEEN_HEALTH_SYSTEM}/CodeSystem/care-plan-sort-order`

export const COMMUNICATION_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CommunicationCategory`
export const COMMUNICATION_REQUEST_CATEGORY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/CommunicationRequestCategory`
export const COMMUNICATION_MEDIUM_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode'
export const AI_THREAD_LANGFUSE_TRACE_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/ai-thread-langfuse-trace-id`

export const SIGNATURE_REQUEST_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/SignatureRequestType`

// Tagged on signature tasks with the originally-requested practitioner id.
// We need a dedup key that survives sign-on-behalf, which mutates Task.owner;
// Task.restriction.recipient is stable but not a searchable parameter on
// hosted Medplum, so we mirror it onto meta.tag (which _tag indexes).
export const SIGNATURE_TASK_RECIPIENT_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/signature-task-recipient`

export const MEDICARE_ID_SYSTEM = 'http://hl7.org/fhir/sid/us-mbi'
export const MEDICAID_ID_SYSTEM =
  'https://www.seenhealth.org/fhir/sid/medicaid-id'
export const SSN_SYSTEM = 'http://hl7.org/fhir/sid/us-ssn'

// Meal Ticket Printer: one Procedure per (participant, local day, meal type).
export const MEAL_SERVED_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/sid/meal-served`
export const MEAL_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/meal-type`

export const METRIPORT_ID_SYSTEM =
  'https://www.metriport.com/fhir/sid/metriport-id'

export const OBSERVATION = `${SEEN_HEALTH_SYSTEM}/CodeSystem/observation`
export const OBSERVATION_INTERPRETATION_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation'

export const LIBRARY_TOPIC = `${SEEN_HEALTH_SYSTEM}/CodeSystem/library-topic`

export const AI_SKILL_ACCESS_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/ai-skill-access`

/** Extension URL for the preferred AI model name stored on a SkillTemplate. */
export const SKILL_PREFERRED_MODEL_EXTENSION_URL = `${SEEN_HEALTH_SYSTEM}/Extension/skill-preferred-model`

/* Extension URL for the evidence fan-out declaration stored on a SkillTemplate:
 * which disciplines' signed records feed the skill's evidence extraction, and
 * which kinds of record to read. */
export const SKILL_EVIDENCE_FANOUT_EXTENSION_URL = `${SEEN_HEALTH_SYSTEM}/Extension/skill-evidence-fanout`

export const SDR_TASK_OUTPUT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/sdr-task-output`

/* `meta.tag` marking a resource as written by an Orbit MCP tool, with the code
 * naming WHICH tool wrote it. Two levels from one coding: `_tag=<system>|`
 * finds everything the MCP created, `_tag=<system>|<code>` narrows to a single
 * writer — which matters because more MCP writers are coming and "created by
 * MCP" alone ages badly.
 *
 * Deliberately records the TOOL, never the practitioner: authorship already
 * lives on the resource (`author`, `meta.author`), and duplicating identity
 * into a tag invites the two disagreeing.
 *
 * On a desk it is load-bearing rather than descriptive: `delete_desk` deletes a
 * desk only while it carries `create_desk`, which is what stops that tool ever
 * reaching a desk a person made in Orbit. Removing this tag from a desk makes
 * it undeletable through MCP; it is not a field to clean up. */
export const MCP_TOOL_ORIGIN = `${SEEN_HEALTH_SYSTEM}/CodeSystem/mcp-tool-origin`
export const McpToolOrigin = {
  CreateDesk: 'create_desk',
  CreateDeskNote: 'create_desk_note',
  CreateParticipantNote: 'create_participant_note',
} as const
export type McpToolOrigin = (typeof McpToolOrigin)[keyof typeof McpToolOrigin]

export const ORBIT_AI_TOOL_ORIGIN = `${SEEN_HEALTH_SYSTEM}/CodeSystem/orbit-ai-tool-origin`
export const OrbitAiToolOrigin = {
  CreateParticipantDraftNote: 'create_participant_draft_note',
} as const
export type OrbitAiToolOrigin =
  (typeof OrbitAiToolOrigin)[keyof typeof OrbitAiToolOrigin]

export const CONDITION_CLINICIAL_STATUS =
  'http://terminology.hl7.org/CodeSystem/condition-clinical'

export const ELATION_CONDITION_CLINICAL_STATUS = `${ELATION_ID_SYSTEM}/CodeSystem/condition-clinical`

export const CONDITION_VERIFICATION_STATUS =
  'http://terminology.hl7.org/CodeSystem/condition-ver-status'

export const CONDITION_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/condition-category`
export const ELATION_CONDITION_RANK_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/elation-condition-rank`

export const APPOINTMENT_CANCELATION_REASON =
  'http://terminology.hl7.org/CodeSystem/appointment-cancellation-reason'

export const REQUIRES_TRANSPORTATION = `${SEEN_HEALTH_SYSTEM}/CodeSystem/requires-transportation`

export const APPOINTMENT_RECURRENCE =
  'https://calendar.google.com/event-recurrence'

export const GCAL_CALENDAR_SYSTEM_URL = 'https://calendar.google.com/calendar'
export const GCAL_EVENT_SYSTEM_URL = 'https://calendar.google.com/event'

export const APPOINTMENT_ORIGIN = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-origin`
export const AppointmentOrigin = {
  GCal: 'gcal',
  Seen: 'seen',
} as const
export type AppointmentOrigin =
  (typeof AppointmentOrigin)[keyof typeof AppointmentOrigin]

// Google's event kind (https://developers.google.com/calendar/api/v3/reference/events —
// `eventType`), carried as a meta tag on synthesized gcal Appointments so the
// calendar can render out-of-office blocks distinctly.
export const APPOINTMENT_GCAL_EVENT_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-gcal-event-type`
export const GCAL_EVENT_TYPE_OUT_OF_OFFICE = 'outOfOffice'

export const APPOINTMENT_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-type`
export const AppointmentType = {
  // GCal/Foundry imports: OOO, holidays, breaks, IDT, etc. Not created in Orbit.
  Busy: 'busy',
  // Seen-origin practitioner blocks booked from the calendar with no participant.
  Admin: 'admin',
  Lunch: 'lunch',
  OutOfOffice: 'out-of-office',
} as const
export type AppointmentType =
  (typeof AppointmentType)[keyof typeof AppointmentType]

// Practitioner-only calendar blocks: booked in Orbit with no participant and
// no HealthcareService. `durationMinutes` seeds the end time from a dragged
// start when the picker has one.
export const PRACTITIONER_BLOCKS = {
  [AppointmentType.Admin]: { display: 'Admin', durationMinutes: 30 },
  [AppointmentType.Lunch]: { display: 'Lunch', durationMinutes: 30 },
  // No fixed duration: keeps a dragged slot, or fills the day when there is none.
  [AppointmentType.OutOfOffice]: {
    display: 'Out of office',
    durationMinutes: undefined,
  },
} as const
export type PractitionerBlockType = keyof typeof PRACTITIONER_BLOCKS

export function isPractitionerBlockType(
  type: string | undefined | null
): type is PractitionerBlockType {
  return !!type && type in PRACTITIONER_BLOCKS
}

// Tags appointments staged by the scheduling agent; code carries the proposal batch id.
export const SCHEDULE_PROPOSAL_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/schedule-proposal`

// Tags an appointment created from a recommended slot that was still on screen
// at submit. The code is fixed; rank, trace, and timing stay off the resource.
export const APPOINTMENT_SLOT_RECOMMENDATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-slot-recommendation`
export const APPOINTMENT_SLOT_RECOMMENDATION_BOOKED = 'booked'

export const APPOINTMENT_TITLE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-title`
export const APPOINTMENT_ALL_DAY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-all-day`
export const APPOINTMENT_CONFERENCE_ID = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-conference-id`
export const APPOINTMENT_CONFERENCE_NOTES = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-conference-notes`
export const APPOINTMENT_LOCATION = `${SEEN_HEALTH_SYSTEM}/CodeSystem/appointment-location`

export const DELIVERY_LOCATION_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/delivery-location-type`
export const DeliveryLocationType = {
  Pickup: 'pickup',
  Dropoff: 'dropoff',
} as const
export type DeliveryLocationType =
  (typeof DeliveryLocationType)[keyof typeof DeliveryLocationType]

export const HEALTHCARE_SERVICE_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/healthcare-service-type`
export const HEALTHCARE_SERVICE_CATEGORY = `${SEEN_HEALTH_SYSTEM}/CodeSystem/healthcare-service-category`

export const PRACTITIONER_LOCATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/practitioner-location`

export const LOCATION_PHYSICAL_TYPE =
  'http://terminology.hl7.org/CodeSystem/location-physical-type'

export const LocationPhysicalType = {
  Room: 'ro',
  Site: 'si',
  Building: 'bu',
  Wing: 'wi',
  Ward: 'wa',
  Level: 'lvl',
  Corridor: 'co',
  Bed: 'bd',
  Vehicle: 've',
  House: 'ho',
  Cabinet: 'ca',
  Road: 'rd',
  Area: 'area',
  Jurisdiction: 'jdn',
  Virtual: 'vi',
} as const
export type LocationPhysicalType =
  (typeof LocationPhysicalType)[keyof typeof LocationPhysicalType]

export const ASTRANA_PORTAL_PROVIDER_ID_SYSTEM =
  'https://provider-portal.astranahealth.com/provider'
export const ASTRANA_PORTAL_AUTHORIZATION_ID_SYSTEM =
  'https://provider-portal.astranahealth.com/authorization'
export const ASTRANA_PORTAL_AUTHORIZATION_DOCUMENT_ID_SYSTEM =
  'https://provider-portal.astranahealth.com/authorization-document'

export const ASTRANA_NETWORK_PROVIDER_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/astrana-network`
export const ASTRANA_NETWORK_EZCAP_PROVIDER_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/astrana-network-ezcap`
// Astrana roster provider "CLASS" code (S=specialist, P=PCP, A=ancillary, ...).
// Proprietary to Astrana; we store the raw code and a best-effort display.
export const ASTRANA_PROVIDER_CLASS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/astrana-provider-class`
// Dedup key for roster-imported PractitionerRoles. The value is the roster
// grouping key: the provider's NPI, or `pid:<PROVIDER ID>` when the roster row
// has no NPI. Keying on this (instead of `practitioner=`) keeps the import from
// touching UI-created org-affiliation roles.
export const ASTRANA_ROSTER_ROLE_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/astrana-roster-role`

export const ELATION_MISFILED_REPORT_TASK_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/elation-misfiled-report-task`
export const ELATION_VISIT_NOTE_SYSTEM = `${ELATION_ID_SYSTEM}/visit-note`
export const ELATION_VISIT_NOTE_TRANSCRIPT_SYSTEM = `${ELATION_VISIT_NOTE_SYSTEM}/scribe-transcript`
export const ELATION_VISIT_NOTE_TEMPLATE_SYSTEM = `${ELATION_VISIT_NOTE_SYSTEM}/template`
export const ELATION_VISIT_NOTE_SECTION_CATEGORY_SYSTEM = `${ELATION_VISIT_NOTE_SYSTEM}/section-category`

export const ELATION_VISIT_NOTE_VITAL_REFERENCE_SYSTEM = `${ELATION_ID_SYSTEM}/visit-note-vital-reference`
export const ELATION_VISIT_NOTE_IMMUNIZATION_DECLINATION_SYSTEM = `${ELATION_ID_SYSTEM}/visit-note-immunization-declination`

export const ELATION_NON_VISIT_NOTE_TYPE_SYSTEM = `${ELATION_ID_SYSTEM}/non-visit-note-type`

export const ELATION_ORDER_REASON_SYSTEM = `${ELATION_ID_SYSTEM}/order-reason`
export const ELATION_ORDER_CENTER_NOTES_SYSTEM = `${ELATION_ID_SYSTEM}/order-center-notes`

export const VISIT_NOTE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/visit-note`
export const VISIT_NOTE_OBSERVATION_SECTION_SYSTEM = `${VISIT_NOTE}/observation-section`
export const VISIT_NOTE_ORDER_SECTION_SYSTEM = `${VISIT_NOTE}/order-section`
export const VISIT_NOTE_CONDITION_SECTION_SYSTEM = `${VISIT_NOTE}/condition-section`
export const VISIT_NOTE_CONTENT_SECTION_SYSTEM = `${VISIT_NOTE}/content`
export const VISIT_NOTE_DOCUMENT_SECTION_SYSTEM = `${VISIT_NOTE}/document-section`
export const VISIT_NOTE_BILLING_SECTION_SYSTEM = `${VISIT_NOTE}/billing-section`

export const COMPOSITION_NOTE_AI_SUMMARY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/composition-note-ai-summary`
export const COMPOSITION_NOTE_AI_SUMMARY_GENERATED_AT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/composition-note-ai-summary-generated-at`
// Written before the paid summary call so an attempt killed by a Lambda timeout
// is distinguishable from a note that was never tried.
export const COMPOSITION_NOTE_AI_SUMMARY_STARTED_AT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/composition-note-ai-summary-started-at`
// Failed summary attempts on the note's current content, reset whenever an
// attempt succeeds or the content changes after the last failure.
export const COMPOSITION_NOTE_AI_SUMMARY_FAILED_ATTEMPTS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/composition-note-ai-summary-failed-attempts`
export const COMPOSITION_NOTE_AI_TITLE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/composition-note-ai-title`
export const COMPOSITION_NOTE_SEARCH_TEXT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/composition-note-search-text`

/**
 * Extension URL for ALL AI-staged suggestions on a composition note — inline
 * editor edits (via `editEditor`) and validated-tool answers (via
 * `proposeValidatedTool`) persist as records in this one staging payload.
 * Each record stores the SEMANTIC operation (never resolved positions): inline
 * ops are re-resolved into live editor proposals on mount, validated-tool
 * records are read by the tool's node view for its banner/prefill. Staging
 * never mutates the note body or creates a QuestionnaireResponse — clinician
 * acceptance does.
 */
export const SUGGESTED_EDITS_STAGING_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/suggested-edits-staging`

export const EXTERNAL_RESOURCE_JSON_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/external-resource-json`

export const EXTERNAL_FILE_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/external-file`
export const N8N_WORKFLOW_ID_SYSTEM = `https://n8n.seen.tech/workflow`
export const N8N_EXECUTION_ID_SYSTEM = `https://n8n.seen.tech/execution`

export const ATTACHMENT_CONTENT_HASH_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/attachment-content-hash`
// Codes are `<version>:<hex digest>`; see documentFingerprint().
export const DOCUMENT_FINGERPRINT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-fingerprint`
// Written only by the document intake step, on a document it saves or merges
// into. Unlike the fingerprint, which a backfill also stamps, it means a
// channel's flag-off path must hand the document back to intake.
export const DOCUMENT_INTAKE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-intake`
export const DOCUMENT_INTAKE_MANAGED = 'managed'

// Carried by a DocumentReference while any attachment's conversion extension is
// `pending`. The extension sits inside `content` and cannot be searched, so this
// tag is how the HEIC watchdog finds stuck conversions without scanning every
// recent document. Keep it in step with the extension: see
// `attachmentConversionTagOperations`.
export const ATTACHMENT_CONVERSION_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/attachment-conversion`
export const ATTACHMENT_CONVERSION_PENDING_TAG_CODE = 'pending'
// Codes are `<version>:<hex digest>` of what an Elation printable report renders.
export const ELATION_REPORT_REVISION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/elation-report-revision`
// A change to this tag's code asks the transcription subscription to run on a
// document whose content did not change (scripts retrying failed transcription).
export const DOCUMENT_TRANSCRIPTION_REQUEST_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-transcription-request`
// Terminal result of a transcription attempt that produced no transcript. It is a
// tag, not an extension, because the transcription subscription fires on any
// extension change and would re-run itself on the write that records the outcome.
export const DOCUMENT_TRANSCRIPTION_OUTCOME_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-transcription-outcome`
// Written before the paid extraction call; the code is the attempt's ISO start
// time. A Lambda timeout or OOM kill skips every error handler, so this tag is
// the only trace of an attempt that died. It is a tag for the same reason as the
// outcome: extension writes re-fire the transcription subscription.
export const DOCUMENT_TRANSCRIPTION_STARTED_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-transcription-started`
// How many transcription attempts on the current attachments ended `failed`;
// the code is the count. The outcome tag is cleared to request a retry, so this
// is the only record that a retry already happened. A tag for the same reason
// as the outcome.
export const DOCUMENT_TRANSCRIPTION_FAILED_ATTEMPTS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-transcription-failed-attempts`

// Written on a DocumentReviewTask when generating its AI title failed. The title
// handler selects tasks still holding the default title, so without this marker
// every later write to the document would pay for another model call. Re-run it
// deliberately by removing the tag.
export const DOCUMENT_REVIEW_TASK_TITLE_FAILED_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-review-task-title-failed`

export const SNOMED_CT_SYSTEM = 'http://snomed.info/sct'
// https://www.findacode.com/pdf.html?id=taxonomy-codes-2024-240
export const NUCC_PROVIDER_TAXONOMY_SYSTEM = 'http://nucc.org/provider-taxonomy'

export const STANDARD_TASK_BUSINESS_STATUS = `${SEEN_HEALTH_SYSTEM}/CodeSystem/standard-task-business-status`

export const SIGNATURE_TASK_BUSINESS_STATUS = `${SEEN_HEALTH_SYSTEM}/CodeSystem/signature-task-business-status`

export const HL7_ALLERGY_INTOLERANCE_LINK =
  'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical'

// https://docs.elationhealth.com/reference/the-allergy-object
// https://arc.net/l/quote/svkinvfe
export const ELATION_ALLERGY_MEDISPANID_SYSTEM = `${ELATION_ID_SYSTEM}/Allergy/medispanid`
export const ELATION_ALLERGY_MEDISPANDNID_SYSTEM = `${ELATION_ID_SYSTEM}/Allergy/medispandnid`

export const ALLERGY_UNKNOWN_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/allergy-unknown`

// ISO-639-1 (language) and ISO-3166-1 (region) codes for FHIR language support
// Format: <language>-<REGION> (e.g., "en-US", "es-MX")
export const LANGUAGE_SYSTEM = 'urn:ietf:bcp:47'

export const HL7_SERVICE_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/service-type'
export const HL7_OBSERVATION_CODE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/observation-category'

export const ICD9_SYSTEM = 'http://hl7.org/fhir/sid/icd-9-cm'
export const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10'
export const ICD10_CM_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm'
export const ICD10_CM_VALUE_SET = 'http://hl7.org/fhir/sid/icd-10-cm/vs'

export const RX_NORM_SYSTEM = 'http://www.nlm.nih.gov/research/umls/rxnorm'

export const CVX_SYSTEM = 'http://hl7.org/fhir/sid/cvx'

export const NCPDPID_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/NCPDPProviderIdentificationNumber'

export const HL7_ORGANIZATION_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/organization-type'

export const REFERRAL_HL7_CODE = '3457005'
export const REFERRAL_HL7_DISPLAY = 'Referral to external specialist'

export const ReferralCode = {
  Treatment: '103696004',
  ProcedureTestingOnly: '108252007',
  Consult: '394733009',
  Imaging: '363679005',
} as const
export type ReferralCode = (typeof ReferralCode)[keyof typeof ReferralCode]

export const ReferralType: Record<
  keyof typeof ReferralCode,
  { code: ReferralCode; description: string }
> = {
  Treatment: {
    code: ReferralCode.Treatment,
    description: 'Referral For Treatment, includes Consult Visit',
  },
  ProcedureTestingOnly: {
    code: ReferralCode.ProcedureTestingOnly,
    description: 'Procedure / Testing Only',
  },
  Consult: {
    code: ReferralCode.Consult,
    description: 'Consult (One Visit Only)',
  },
  Imaging: {
    code: ReferralCode.Imaging,
    description: 'Imaging',
  },
}

export const GROUP_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/group-type`

export const ORDER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/order-type`

export const SIGNED_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/signed-date`
export const SIGNED_BY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/signed-by`

export const EDITED_DATE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/edited-date`

export const DOCUMENT_CATEGORY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-category`
export const DOCUMENT_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-type`
export const DOCUMENT_SOURCE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-source`

export const IDEMPOTENCY_KEY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/idempotency-key`

export const PARTICIPANT_FLAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/participant-flag`
export const FLAG_NOTES_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/flag-notes`

export const PARTICIPANT_LABEL_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/participant/label`

export const ORDER_LABEL_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/order/label`

export const PRACTITIONER_RECENT_SELECTIONS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/practitioner-recent-selections`

export const PRACTITIONER_SERVICE_CATEGORY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/practitioner-service-category`

export const PRACTITIONER_PREFERRED_SPECIALTY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/practitioner-preferred-specialty`

export const NPI_SYSTEM = 'http://hl7.org/fhir/sid/us-npi'
export const DEA_SYSTEM = 'http://hl7.org/fhir/sid/us-dea'
export const US_STATE_LICENSE_SYSTEM =
  'http://hl7.org/fhir/sid/us-state-license'

export const ASTRANA_NETWORK_IPA_SYSTEM =
  'https://www.seenhealth.org/fhir/identifiers/astrana-network-ipa'

// Tag system carrying a Practitioner/Organization's derived network status.
// Non-network providers are excluded from clinician-facing search (order
// performer pickers, etc.) but remain visible in the fax recipient list and
// the admin provider catalog.
export const PROVIDER_NETWORK_STATUS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/provider-network-status`

// Derived three-state network status. `internal` = Seen staff; `in-network` =
// contracted (Astrana or a direct Seen arrangement); `non-network` =
// everything else. Only `non-network` is stamped explicitly today (legacy
// fax-only recipients); internal/in-network are derived from other markers
// until the backfill stamps them. See ExtendedPractitioner/
// ExtendedOrganization.networkStatus.
export const ProviderNetworkStatus = {
  Internal: 'internal',
  InNetwork: 'in-network',
  NonNetwork: 'non-network',
} as const
export type ProviderNetworkStatus =
  (typeof ProviderNetworkStatus)[keyof typeof ProviderNetworkStatus]

export const PROVIDER_NETWORK_STATUS_DISPLAY: Record<
  ProviderNetworkStatus,
  string
> = {
  internal: 'Internal',
  'in-network': 'In-network',
  'non-network': 'Non-network',
}

export const NON_NETWORK_TAG_CODE = ProviderNetworkStatus.NonNetwork

export const LEAD_FLAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/lead-flag`

export const BASIC_CODE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/basic-code`

export const RESOURCE_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/resource-type`

export const EXTENDED_RESOURCE_SUBSCRIBERS = `${SEEN_HEALTH_SYSTEM}/CodeSystem/extended-resource-subscribers`

export const ZOOM_SYSTEM = `https://www.zoom.us`
export const ZOOM_CALL_LOG_ID_SYSTEM = `${ZOOM_SYSTEM}/call-log-id`
export const ZOOM_CALL_ID_SYSTEM = `${ZOOM_SYSTEM}/call-id`
export const ZOOM_CALL_RECORDING_ID_SYSTEM = `${ZOOM_SYSTEM}/call-recording-id`

export const CARE_PLAN_SNAPSHOT_SECTION_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/care-plan-snapshot-section-type`

export const CARE_PLAN_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CarePlan/tag`

export const SIGNATURE_VOIDED_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Provenance/signature-voided`

export const STANDARD_TASK_LABEL_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Task/standard-task-label`
export const STANDARD_TASK_SEND_INBOX_NOTIFICATION_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/task-send-inbox-notification`

export const TASK_LABEL_SYSTEM_MANAGED_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/task-label-system-managed-id`

export const NOTE_LABEL_SYSTEM_MANAGED_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/note-label-system-managed-id`

export const NOTE_LABEL_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/note/label`

export const LABEL_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Basic/label-identifier`
export const LABEL_COLOR_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/label-color`
export const LABEL_STATUS_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Basic/label-status`

export const VIEW_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Basic/view-identifier`
export const VIEW_ACCESS_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Basic/view-access`
export const VIEW_STATUS_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Basic/view-status`

export const PARTICIPANT_VIEW_FILTERS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/participant-view-filters`
export const PARTICIPANT_VIEW_DISPLAY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/participant-view-display`

export const ORDER_VIEW_FILTERS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/order-view-filters`

export const TASK_VIEW_GROUPING_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/task-view-grouping`
export const TASK_VIEW_FILTERS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/task-view-filters`

export const DESK_ACCESS_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/DocumentReference/desk-access`

export const SENSITIVE_QUESTIONNAIRE_ITEM = `${SEEN_HEALTH_SYSTEM}/Questionnaire/sensitive-item`

export const CPT_CODE_SYSTEM = 'http://www.ama-assn.org/go/cpt'
export const CPT_CODE_VALUE_SET = `${CPT_CODE_SYSTEM}/vs`

export const PROCEDURE_CODE_VALUE_SET = `${SEEN_HEALTH_SYSTEM}/ValueSet/procedure-codes`

export const LOINC_CODE_SYSTEM = 'http://loinc.org'
export const LOINC_CODE_VALUE_SET = `${LOINC_CODE_SYSTEM}/vs`

export const IMAGING_TESTS_VALUE_SET = `${SEEN_HEALTH_SYSTEM}/ValueSet/imaging-tests`
export const IMAGING_TESTS_CUSTOM_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/imaging-tests-custom`

export const ORDER_SPECIALTY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/order-specialty`

export const ORDER_AI_TITLE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/order-ai-title`

export const MEDICATION_REQUEST_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-type`
export const MedicationRequestType = {
  Prescription: 'prescription',
  OverTheCounter: 'over-the-counter',
} as const
export type MedicationRequestType =
  (typeof MedicationRequestType)[keyof typeof MedicationRequestType]

/* Comprehensive pharmacy transmission tracking (state, type, detail, timestamp) */
export const MEDICATION_REQUEST_PHARMACY_TRANSMISSION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-pharmacy-transmission`

/* Legacy pharmacy transmission issue tracking (backward compatibility) */
export const MEDICATION_REQUEST_PHARMACY_TRANSMISSION_ISSUE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-pharmacy-transmission-issue`
export const MEDICATION_REQUEST_PHARMACY_TRANSMISSION_ISSUE_RESOLVED_AT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-pharmacy-transmission-issue-resolved-at`

export const MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/MedicationRequest/stopped-encounter`

export const MEDICATION_REQUEST_DISCONTINUED_BY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/MedicationRequest/discontinued-by`
export const MEDICATION_REQUEST_STATUS_REASON_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-status-reason`

/* Elation medication thread tracking - links related prescriptions (New + Refills + DoseChanges) */
export const MEDICATION_REQUEST_THREAD_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-thread-id`

/* Elation order type - indicates whether this is a new prescription, refill, or dose adjustment */
export const MEDICATION_REQUEST_ORDER_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-order-type`
export const MedicationRequestOrderType = {
  New: 'New',
  Refill: 'Refill',
  DoseChange: 'DoseChange',
} as const
export type MedicationRequestOrderType =
  (typeof MedicationRequestOrderType)[keyof typeof MedicationRequestOrderType]

/* Elation thread permanence - indicates chronic (ongoing) vs acute (short-term) medication */
export const MEDICATION_REQUEST_IS_PERMANENT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-is-permanent`

/* Pharmacy reception tracking - captures status from GraneRx/filler side (HL7 confirmations) */
export const MEDICATION_REQUEST_PHARMACY_RECEPTION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-pharmacy-reception`

/* Previous dispenser for an Orbit-created refill draft. Elation's order JSON does not carry it. */
export const MEDICATION_REQUEST_PREVIOUS_DISPENSER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-previous-dispenser`

export const MEDICATION_REQUEST_GRANERX_ORDER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/medication-request-granerx-order`
export const GRANERX_HL7_MESSAGE_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/granerx-hl7-message`
export const GRANERX_FILLER_ORDER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/granerx-filler-order`

// Dedupe key for Tasks the nightly scheduling-rules revalidation sweep
// files, so re-running the sweep does not re-create the same finding.
export const SCHEDULING_REVALIDATION_TASK_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/scheduling-revalidation`

// Identifies a MedicationRefillTask by participant + cutoff, so re-running the
// pilot script or cron against the same participant/cutoff pair is idempotent.
export const MEDICATION_REFILL_TASK_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/medication-refill-task`

export const DOCUMENT_VISIBILITY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/document-visibility`
export const DOCUMENT_VISIBILITY_SYSTEM_INTERNAL = 'system-internal'

export const DOCUMENT_REFERENCE_TRANSCRIPTION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-reference-transcription`
export const DOCUMENT_REFERENCE_TRANSCRIPTION_PAGES_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-reference-transcription-pages`
export const DOCUMENT_REFERENCE_TRANSCRIPTION_CONFIDENCE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-reference-transcription-confidence`
export const DOCUMENT_REFERENCE_CONTAINS_HANDWRITING_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-reference-contains-handwriting`
export const DOCUMENT_REFERENCE_AI_SUMMARY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-reference-ai-summary`

export const MEAL_PLATING_QUEUE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/plating-queue`
export const MEAL_SERVING_QUEUE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/serving-queue`

export const DOCUMENT_CATEGORY_DESCRIPTION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-category-description`

export const TRACE_ID_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/trace-id`

export const QUEST_ACCOUNT_NUMBER_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/quest-account-number`

export const QUEST_ACCESSION_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/identifiers/quest-accession`

// Lab copy grouping. Every member of a group of two or more copies of one Quest
// result carries a group tag whose code is the Quest accession (the group is
// that accession within one participant). Every member except the primary copy
// that still has no open review task also carries a copy-of tag whose code is
// the primary's reference string. Readers hide a document that has any copy-of
// tag; a document without one is always shown.
export const DOCUMENT_LAB_COPY_GROUP_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-lab-copy-group`
// The participant a group was formed under (code: the participant's reference
// string). A document moved to another participant keeps it, which is how the
// group it left finds out it lost a member.
export const DOCUMENT_LAB_COPY_PARTICIPANT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-lab-copy-participant`
export const DOCUMENT_LAB_COPY_OF_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-lab-copy-of`
// A copy that would be hidden but has an open review task carries this, so the
// daily sweep can find it again after the task closes.
export const DOCUMENT_LAB_COPY_PINNED_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-lab-copy-pinned`
// A Quest PDF the lab copy reconciler filed to a participant after it arrived
// with none. With lab copy grouping off, readers leave these out so the
// participant's Documents view is what it was before the feature.
export const DOCUMENT_LAB_COPY_FILED_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/document-lab-copy-filed`

export const LIST_TYPE = `${SEEN_HEALTH_SYSTEM}/CodeSystem/list-type`

export const LOINC_OBSERVATIONS_VALUE_SET = `${SEEN_HEALTH_SYSTEM}/ValueSet/loinc-observations`

export const IDENTIFIER_TYPE = 'http://terminology.hl7.org/CodeSystem/v2-0203'

export const IdentifierType = {
  Placer: 'PLAC',
  Filler: 'FILL',
  Accession: 'ACSN',
  AccountNumber: 'AN',
} as const
export type IdentifierType =
  (typeof IdentifierType)[keyof typeof IdentifierType]

export const identifierTypePlacer = codeableConcept({
  system: IDENTIFIER_TYPE,
  code: IdentifierType.Placer,
  display: 'Placer',
})

export const identifierTypeFiller = codeableConcept({
  system: IDENTIFIER_TYPE,
  code: IdentifierType.Filler,
  display: 'Filler',
})

export const identifierTypeAccession = codeableConcept({
  system: IDENTIFIER_TYPE,
  code: IdentifierType.Accession,
  display: 'Accession',
})

export const identifierTypeAccountNumber = codeableConcept({
  system: IDENTIFIER_TYPE,
  code: IdentifierType.AccountNumber,
  display: 'Account Number',
})

export const IMMUNIZATION_PERFORMER_FUNCTION_SYSTEM = `http://terminology.hl7.org/CodeSystem/v2-0443`

export const ImmunizationPerformerFunction = {
  AdministeringProvider: 'AP',
  OrderingProvider: 'OP',
} as const
export type ImmunizationPerformerFunction =
  (typeof ImmunizationPerformerFunction)[keyof typeof ImmunizationPerformerFunction]

export const immunizationPerformerFunctionAdministeringProvider =
  codeableConcept({
    system: IMMUNIZATION_PERFORMER_FUNCTION_SYSTEM,
    code: ImmunizationPerformerFunction.AdministeringProvider,
    display: 'Administering Provider',
  })
export const immunizationPerformerFunctionOrderingProvider = codeableConcept({
  system: IMMUNIZATION_PERFORMER_FUNCTION_SYSTEM,
  code: ImmunizationPerformerFunction.OrderingProvider,
  display: 'Ordering Provider',
})

export const DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/DocumentReference/predicted-relation`
export const DOCUMENT_REFERENCE_PREDICTED_RELATIONS_EXTENSION = `${SEEN_HEALTH_SYSTEM}/DocumentReference/predicted-relations-data`
// Tag code written when the agent ran and produced no Patient/ServiceRequest
// hits. Same system as real predictions so `_tag:not system|` still excludes
// the document; not a resource reference, so `predictedRelations` hides it.
export const DOCUMENT_REFERENCE_PREDICTED_RELATIONS_NONE_CODE = 'none'
// Tag code written when the prediction run itself threw. Same system, so the
// subscription's `_tag:not system|` and `hasPredictedRelationsAttempt` treat
// the document as attempted and a later write cannot trigger another paid
// model call. Re-run it deliberately by removing the tag.
export const DOCUMENT_REFERENCE_PREDICTED_RELATIONS_FAILED_CODE = 'failed'
// Written before the paid prediction call; the code is the attempt's ISO start
// time. Deliberately not in the predicted-relation system: that system's
// presence is what the subscription's `_tag:not` criteria and
// `hasPredictedRelationsAttempt` read as "attempted", so a started marker there
// would suppress the real attempt.
export const DOCUMENT_REFERENCE_PREDICTION_STARTED_SYSTEM = `${SEEN_HEALTH_SYSTEM}/DocumentReference/predicted-relation-started`
// How many prediction attempts ended `failed`; the code is the count. Outside
// the predicted-relation system for the same reason as the started marker, and
// kept when a retry clears the `failed` tag so the retry knows it is the last.
export const DOCUMENT_REFERENCE_PREDICTION_FAILED_ATTEMPTS_SYSTEM = `${SEEN_HEALTH_SYSTEM}/DocumentReference/predicted-relation-failed-attempts`
export const DOCUMENT_REFERENCE_INLINE_PASTE_PROCESSING_SYSTEM = `${SEEN_HEALTH_SYSTEM}/DocumentReference/inline-paste-processing`

// Per-entry AI summaries carried on a queued-edits proposal Bundle, as a
// repeating complex extension on `Bundle.meta.extension` (Bundle is not a
// DomainResource, so it has no top-level `extension`): sub-extension `target`
// keys the bundle entry (fullUrl / `ResourceType/id` / bare resourceType) and
// `summary` holds the text. See packages/core/src/queued-edits/ai-summaries.ts.
export const QUEUED_EDITS_AI_SUMMARY_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-ai-summary`

// Stamped by SeenMedplumClient.executeBatch onto each PATCH response entry: the
// versionId the patch was ultimately conditioned on (`test /meta/versionId`)
// after fhirpath→json conversion and any internal version-conflict rebase. This
// is the authoritative "applied from" base version — the only place the caller
// can read it, since executeBatch alone resolves it. Read it back with
// `appliedFromVersionRefs(responseBundle)`.
export const BATCH_ENTRY_BASE_VERSION_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/batch-entry-base-version`

// Task.input typing for queued-edits review tasks: the input references the
// persisted proposal Bundle. The Task is pure orchestration — the proposal
// Bundle is the self-contained source of truth and carries the reviewer's
// per-entry decisions itself (see QUEUED_EDITS_ENTRY_DECISION_EXTENSION).
export const QUEUED_EDITS_TASK_IO_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/queued-edits-task-io`
export const QueuedEditsTaskIO = {
  PROPOSED_BUNDLE: 'proposed-bundle',
  // Deprecated: decisions now live on the proposal Bundle's entries
  // (QUEUED_EDITS_ENTRY_DECISION_EXTENSION), not Task.output. Retained only so
  // the const's other members keep their codes stable.
  APPROVED_ENTRY: 'approved-entry',
  REJECTED_ENTRY: 'rejected-entry',
  FINAL_COMMITTER: 'final-committer',
} as const
export type QueuedEditsTaskIO =
  (typeof QueuedEditsTaskIO)[keyof typeof QueuedEditsTaskIO]

// meta.tag stamped on every resource a queued-edits review applied, pointing
// back at the durable review record — the proposal Bundle (`Bundle/<id>`), or
// the QueuedEditsReviewTask (`Task/<id>`) when one orchestrates the review. The
// tag's `code` is that reference string. Activity feeds read this off the
// resource version that introduced the change to render a "as part of … review"
// link. Because tags persist across subsequent versions, only the version that
// first carried the tag (vs the prior version) should surface the link.
// (String value retains the historical `-origin-task` suffix so resources
// applied before the Bundle-centric refactor still resolve their backlink.)
export const QUEUED_EDITS_ORIGIN_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/queued-edits-origin-task`

// meta.tag stamped by backend batch flows (disenrollment processing/cleanup
// crons and legacy pre-origin-tag proposal bundles) marking which batch
// operation touched a resource. Bookkeeping only — never a domain change — so
// the queued-edits already-applied comparison ignores it alongside the origin
// tag above.
export const BATCH_OPERATION_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/batch-operation`

// meta.tag stamped on a Condition the Elation sync handler could not represent
// as an Elation problem, so the sidecar can surface it. The sync handler
// reconciles it on every delivery: `code` is the machine reason
// (`external-cause` | `no-mapping`) and `display` is "<icd10>: <human reason>".
// The ICD-10 is carried in `display` so a recode that still fails changes the
// display, which the handler detects as a change and re-alerts on.
export const ELATION_UNSYNCABLE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/elation-unsyncable`

// meta.tag marking the last inbound Elation-sync divergence state observed for a
// Condition (pull-side guard). `code` is a compact signature of the diverged
// incoming coding + outcome; the alert fires only when this marker transitions,
// so a persistently-diverged Condition alerts once, not on every daily pull.
export const ELATION_PULL_DIVERGENCE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/CodeSystem/elation-pull-divergence`

// Audit extension carried on each per-entry decision — who made the decision
// and when. Used both on a proposal Bundle's `entry[*].extension` (the current
// home of decisions, wrapped in QUEUED_EDITS_ENTRY_DECISION_EXTENSION) and on
// the bundle-level final-committer extension. Sub-extensions: `decidedBy`
// (valueReference → Practitioner), `decidedAt` (valueDateTime), and
// `fromVersion` (valueReference → the pinned `ResourceType/id/_history/{vid}`
// the entry's diff was computed against). `fromVersion` pins the completed-diff
// baseline for both outcomes: for an approved entry it is the version the change
// was applied from; for a rejected entry it is the version the proposed (but
// discarded) diff was shown against. Reading the latest version instead would
// drift as the resource is edited after the review.
export const QUEUED_EDITS_DECISION_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-decision`
export const QUEUED_EDITS_DECISION_BY = 'decidedBy'
export const QUEUED_EDITS_DECISION_AT = 'decidedAt'
export const QUEUED_EDITS_DECISION_FROM_VERSION = 'fromVersion'

// Per-entry reviewer decision on a queued-edits proposal Bundle, carried on
// `Bundle.entry[*].extension` so the Bundle is the self-contained source of
// truth (survives a page refresh; needs no Task). One complex extension per
// decided entry — the entry it sits on is the key, so no separate handle field.
// Sub-extensions: `decision` (valueCode `approved` | `rejected`) plus the
// shared decision-audit sub-extensions (QUEUED_EDITS_DECISION_BY / _AT /
// _FROM_VERSION). Stripped from the apply bundle before executeBatch so the
// review metadata never reaches the server write (see
// packages/core/src/queued-edits/decisions.ts).
export const QUEUED_EDITS_ENTRY_DECISION_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-entry-decision`
export const QUEUED_EDITS_DECISION_OUTCOME = 'decision'

// Proposer-supplied (AI/backend) intent label for a single queued-edits entry,
// carried on `Bundle.entry[*].extension` as `valueString`. One short
// plain-English phrase describing WHY the entry changes (e.g. "Add the Onsite
// tag"), rendered by the review card in place of the raw "N changes" count —
// the field count isn't meaningful to reviewers; the intent is. Distinct from
// QUEUED_EDITS_AI_SUMMARY_EXTENSION (one bundle-level overview of the whole
// proposal): this is per-entry. Stripped from the apply bundle alongside the
// decision extensions so review-display metadata never reaches the server write.
export const QUEUED_EDITS_ENTRY_INTENT_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-entry-intent`

// Proposer-supplied group label for a single queued-edits entry, carried on
// `Bundle.entry[*].extension` as `valueString`. Entries sharing the same label
// form one logical unit in the review UI (e.g. a care plan with its goals,
// interventions, and signature tasks), rendered under a shared group header
// with group-level approve/reject. The label doubles as the group key —
// grouping is by exact string match. Decisions remain per-entry underneath, so
// apply/audit machinery is unchanged. Stripped from the apply bundle alongside
// the decision extensions so review-display metadata never reaches the server
// write.
export const QUEUED_EDITS_ENTRY_GROUP_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-entry-group`

// Bundle-level record of which entries the current AI summary + intent labels
// intentionally omitted (`Bundle.meta.extension`, valueString = JSON array of
// entry handles). Written when the summary is (re)generated after some entries
// went stale — their proposed change was already applied outside the review —
// so the summary describes only the entries still under review. The review
// surface compares this against the currently-stale entry set to decide whether
// enough drift accumulated to regenerate the summary, and to avoid regenerating
// again for the same drift. Absent = the summary covers every entry.
export const QUEUED_EDITS_AI_SUMMARY_OMITTED_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-ai-summary-omitted`

// Bundle-level record of who executed the final application of the approved
// changes, on `Bundle.meta.extension` (Bundle has no top-level `extension`).
// Distinct from each entry's `decidedBy`: this is the single practitioner who
// confirmed and committed the batch. Sub-extensions: `decidedBy`
// (valueReference → Practitioner) and `decidedAt` (valueDateTime).
export const QUEUED_EDITS_FINAL_COMMITTER_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/queued-edits-final-committer`

// AuditEvent.type for the record of where a queued-edits proposal came from.
// One event per proposed Bundle, written when the proposal is staged. The
// event uses only native AuditEvent fields (no custom extensions) so it stays
// searchable by `entity` and `type`. See
// packages/core/src/models/audit/QueuedEditsProposalAuditEvent.ts.
export const QUEUED_EDITS_AUDIT_EVENT_TYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/queued-edits-audit-event-type`
export const QUEUED_EDITS_AUDIT_EVENT_PROPOSED_CODE = 'queued-edits-proposed'

// AuditEvent.subtype coding system for how a proposal was initiated. The
// generic codes (automated, assisted, direct) are the `ProposalMethod` const in
// the model; product and vendor identities belong in AuditEvent.source.site.
export const QUEUED_EDITS_PROPOSAL_METHOD_SYSTEM = `${SEEN_HEALTH_SYSTEM}/queued-edits-proposal-method`

export const FAX_DIRECTION_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/fax-direction`

// Per-fax override of the destination number, on Communication. When present,
// the fax pipeline sends here instead of the recipient's primaryFaxNumber.
export const DESTINATION_FAX_NUMBER_EXTENSION = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/destination-fax-number`

export const FaxDirection = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
} as const

export type FaxDirection = (typeof FaxDirection)[keyof typeof FaxDirection]

// https://hl7.org/fhir/R4/codesystem-immunization-recommendation-status.html
export const IMMUNIZATION_RECOMMENDATION_STATUS_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/immunization-recommendation-status'
export const ImmunizationRecommendationStatus = {
  Due: 'due',
  Overdue: 'overdue',
  Complete: 'complete',
} as const
export type ImmunizationRecommendationStatus =
  (typeof ImmunizationRecommendationStatus)[keyof typeof ImmunizationRecommendationStatus]

export const immunizationRecommendationStatusComplete = codeableConcept({
  system: IMMUNIZATION_RECOMMENDATION_STATUS_SYSTEM,
  code: ImmunizationRecommendationStatus.Complete,
  display: 'Complete',
})
export const immunizationRecommendationStatusDue = codeableConcept({
  system: IMMUNIZATION_RECOMMENDATION_STATUS_SYSTEM,
  code: ImmunizationRecommendationStatus.Due,
  display: 'Due',
})
export const immunizationRecommendationStatusOverdue = codeableConcept({
  system: IMMUNIZATION_RECOMMENDATION_STATUS_SYSTEM,
  code: ImmunizationRecommendationStatus.Overdue,
  display: 'Overdue',
})

export const UCUM_SYSTEM = 'http://unitsofmeasure.org'

export const SUBSCRIBER_RELATIONSHIP_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/subscriber-relationship'

export const subscriberRelationshipSelf = codeableConcept({
  system: SUBSCRIBER_RELATIONSHIP_SYSTEM,
  code: 'self',
  display: 'Self',
})

export const DATA_OPERATION =
  'http://terminology.hl7.org/CodeSystem/v3-DataOperation'
export const DataOperation = {
  Cancel: 'CANCEL',
} as const
export type DataOperation = (typeof DataOperation)[keyof typeof DataOperation]
export const dataOperationCancel = codeableConcept({
  system: DATA_OPERATION,
  code: DataOperation.Cancel,
  display: 'Cancel',
})

/**
 * Extension URL for the AI-suggested-edits staging payload stored on a
 * `Basic` resource (`SuggestionStagingBasic`). The value is a JSON-encoded
 * `SuggestedEditsStaging` (`StagedSuggestion[]`), scoped private to the
 * practitioner who generated it.
 */
export const AI_SUGGESTED_EDITS_STAGING_PAYLOAD_SYSTEM = `${SEEN_HEALTH_SYSTEM}/StructureDefinition/ai-suggested-edits-staging-payload`

/** Basic.code value for the per-practitioner AI suggestion staging resource. */
export const AI_SUGGESTED_EDITS_STAGING_CODE = 'ai-suggested-edits-staging'
